import { useState, useEffect, useCallback, useRef } from 'react';
import { X, Loader2, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

/**
 * Modal de Progreso de Procesos
 * 
 * Componente reutilizable para mostrar el progreso de procesos largos
 * (sincronización, liquidación, decomisión) con estadísticas en tiempo real
 * 
 * Props:
 * - isOpen: boolean - Controla la visibilidad del modal
 * - onClose: () => void - Callback cuando se cierra el modal
 * - jobId: string - UUID del job a monitorear
 * - processName: string - Nombre del proceso para mostrar al usuario
 */
export default function ProcessProgressModal({ isOpen, onClose, jobId, processName = 'Proceso' }) {
  const [jobStatus, setJobStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  // Función para obtener el estado del job desde la API
  const fetchJobStatus = useCallback(async () => {
    if (!jobId) return;

    try {
      const response = await axios.get(`/api/liquidaciones/jobs/${jobId}`);
      setJobStatus(response.data);
      setError(null);
      setLoading(false);

      // Si el job terminó (completed, cancelled, error), detener polling
      if (['completed', 'cancelled', 'error'].includes(response.data.estado)) {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    } catch (err) {
      console.error('Error fetching job status:', err);
      setError('Error obteniendo estado del proceso');
      setLoading(false);
    }
  }, [jobId]);

  // Polling cada 1 segundo mientras el modal está abierto
  useEffect(() => {
    if (!isOpen || !jobId) return;

    // Fetch inicial
    fetchJobStatus();

    // Iniciar polling
    intervalRef.current = setInterval(fetchJobStatus, 1000);

    // Cleanup al desmontar o cambiar jobId/isOpen
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isOpen, jobId, fetchJobStatus]);

  // Función para cancelar el job
  const handleCancel = async () => {
    if (!jobId) return;

    if (!confirm('¿Estás seguro de que deseas cancelar este proceso?')) {
      return;
    }

    try {
      await axios.post(`/api/liquidaciones/jobs/${jobId}/cancel`);
      // El polling detectará el cambio de estado automáticamente
    } catch (err) {
      console.error('Error cancelando job:', err);
      alert('Error cancelando el proceso. Intenta de nuevo.');
    }
  };

  // Función para cerrar el modal
  const handleClose = () => {
    // Limpiar polling
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    onClose();
  };

  // No renderizar si no está abierto
  if (!isOpen) return null;

  // Calcular porcentaje de progreso
  const progressPercentage = jobStatus?.progreso_total > 0
    ? Math.round((jobStatus.progreso_actual / jobStatus.progreso_total) * 100)
    : 0;

  // Determinar icono y color según estado
  const getStatusIcon = () => {
    if (loading || !jobStatus) {
      return <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />;
    }

    switch (jobStatus.estado) {
      case 'running':
        return <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />;
      case 'completed':
        return <CheckCircle2 className="w-8 h-8 text-green-500" />;
      case 'cancelled':
        return <AlertCircle className="w-8 h-8 text-yellow-500" />;
      case 'error':
        return <XCircle className="w-8 h-8 text-red-500" />;
      default:
        return <Loader2 className="w-8 h-8 text-gray-500 animate-spin" />;
    }
  };

  const getStatusText = () => {
    if (loading || !jobStatus) return 'Cargando...';
    
    switch (jobStatus.estado) {
      case 'running':
        return 'En progreso';
      case 'completed':
        return 'Completado';
      case 'cancelled':
        return 'Cancelado';
      case 'error':
        return 'Error';
      default:
        return jobStatus.estado;
    }
  };

  const getStatusColor = () => {
    if (!jobStatus) return 'text-gray-600';
    
    switch (jobStatus.estado) {
      case 'running':
        return 'text-blue-600';
      case 'completed':
        return 'text-green-600';
      case 'cancelled':
        return 'text-yellow-600';
      case 'error':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-800">{processName}</h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            title="Cerrar"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Estado y icono */}
          <div className="flex items-center space-x-4">
            {getStatusIcon()}
            <div>
              <p className={`text-xl font-semibold ${getStatusColor()}`}>
                {getStatusText()}
              </p>
              {jobStatus?.mensaje_progreso && (
                <p className="text-sm text-gray-600 mt-1">
                  {jobStatus.mensaje_progreso}
                </p>
              )}
            </div>
          </div>

          {/* Barra de progreso */}
          {jobStatus?.estado === 'running' && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-gray-600">
                <span>
                  {jobStatus.progreso_actual?.toLocaleString('es-ES') || 0} / {jobStatus.progreso_total?.toLocaleString('es-ES') || 0} registros
                </span>
                <span className="font-semibold">{progressPercentage}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                <div
                  className="bg-blue-600 h-full transition-all duration-500 ease-out rounded-full"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>
          )}

          {/* Estadísticas en tiempo real */}
          {jobStatus && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {/* Velocidad */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <p className="text-xs text-blue-600 font-semibold uppercase mb-1">
                  Velocidad
                </p>
                <p className="text-2xl font-bold text-blue-900">
                  {jobStatus.velocidad_registros_seg?.toFixed(1) || '0.0'}
                </p>
                <p className="text-xs text-blue-700 mt-1">reg/seg</p>
              </div>

              {/* Exitosos */}
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <p className="text-xs text-green-600 font-semibold uppercase mb-1">
                  Exitosos
                </p>
                <p className="text-2xl font-bold text-green-900">
                  {jobStatus.registros_exitosos?.toLocaleString('es-ES') || 0}
                </p>
                <p className="text-xs text-green-700 mt-1">registros</p>
              </div>

              {/* Fallidos */}
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <p className="text-xs text-red-600 font-semibold uppercase mb-1">
                  Fallidos
                </p>
                <p className="text-2xl font-bold text-red-900">
                  {jobStatus.registros_fallidos?.toLocaleString('es-ES') || 0}
                </p>
                <p className="text-xs text-red-700 mt-1">registros</p>
              </div>
            </div>
          )}

          {/* Resultado final (cuando está completado) */}
          {jobStatus?.estado === 'completed' && jobStatus.resultado_final && (
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-sm font-semibold text-green-800 mb-2">
                📊 Resumen Final
              </p>
              <div className="text-sm text-green-900 space-y-1">
                {Object.entries(jobStatus.resultado_final).map(([key, value]) => {
                  // Ignorar arrays de errores en el resumen
                  if (Array.isArray(value)) return null;
                  
                  return (
                    <div key={key} className="flex justify-between">
                      <span className="font-medium">
                        {key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}:
                      </span>
                      <span>{typeof value === 'number' ? value.toLocaleString('es-ES') : value}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Mensaje de error (cuando hay error) */}
          {jobStatus?.estado === 'error' && jobStatus.mensaje_error && (
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <p className="text-sm font-semibold text-red-800 mb-2">
                ❌ Error
              </p>
              <p className="text-sm text-red-900">
                {jobStatus.mensaje_error}
              </p>
            </div>
          )}

          {/* Aviso de no refrescar */}
          {jobStatus?.estado === 'running' && (
            <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
              <p className="text-sm text-yellow-800">
                ⚠️ <strong>Por favor, no refresques la página</strong> mientras el proceso está en ejecución.
                Puedes cerrar este modal y el proceso continuará en background.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
          {jobStatus?.estado === 'running' && (
            <button
              onClick={handleCancel}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
            >
              Cancelar Proceso
            </button>
          )}
          
          <button
            onClick={handleClose}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              jobStatus?.estado === 'running'
                ? 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {jobStatus?.estado === 'running' ? 'Cerrar (continúa en background)' : 'Cerrar'}
          </button>
        </div>
      </div>
    </div>
  );
}
